// WAP Phase 2 (S4.1) — runtime per-bank theming (SHARED-DOMAIN re-skin).
// Applies the farmer's organization branding to the CCS design-system CSS variables
// at runtime. Branding is ADDITIVE: with no branding we remove the overrides and the
// static :root defaults (the generic AGXL green) take over — never a broken default.

export interface OrgBranding {
  primary_color?: string | null;
  accent_color?: string | null;
  logo_text?: string | null;
  logo_url?: string | null;
}

// The design-system vars the brand drives. Kept deliberately small (the high-value,
// app-wide visible ones); --primary-foreground is left as-is (white) so text on the
// brand colour stays legible across banks.
const PRIMARY_VARS = ["--primary", "--ring", "--primary-light", "--sidebar-primary"];
const ACCENT_VARS = ["--accent"];

// Convert "#RRGGBB" / "#RGB" to a shadcn HSL triple string "H S% L%"
// (CCS CSS uses hsl(var(--primary)), so the var value must be the bare triple).
export function hexToHslTriple(hex: string | null | undefined): string | null {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const l = (max + min) / 2;
  let hue = 0, s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Remove all brand overrides → fall back to the static :root defaults (generic CCS).
export function clearBranding(): void {
  const root = document.documentElement;
  [...PRIMARY_VARS, ...ACCENT_VARS].forEach((v) => root.style.removeProperty(v));
}

// Apply org branding to the live CSS variables. Null/invalid → generic default.
export function applyBranding(b: OrgBranding | null | undefined): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!b) { clearBranding(); return; }
  const primary = hexToHslTriple(b.primary_color);
  const accent = hexToHslTriple(b.accent_color);
  // Apply only what converts cleanly; leave the rest at the default (never break).
  if (primary) PRIMARY_VARS.forEach((v) => root.style.setProperty(v, primary));
  else PRIMARY_VARS.forEach((v) => root.style.removeProperty(v));
  if (accent) ACCENT_VARS.forEach((v) => root.style.setProperty(v, accent));
  else ACCENT_VARS.forEach((v) => root.style.removeProperty(v));
}
